/**
 * Color conversion and contrast utilities for tenant theme configuration.
 *
 * Used to derive readable title colors from tenant primary brand colors
 * while meeting WCAG contrast requirements on white backgrounds.
 */

/**
 * RGB color channel values (0–255)
 */
export interface RGB {
	r: number;
	g: number;
	b: number;
}

/**
 * HSL color components for internal conversion
 */
interface HSL {
	h: number;
	s: number;
	l: number;
}

/** Reference white background for contrast checks */
const WHITE: RGB = { r: 255, g: 255, b: 255 };

/** WCAG AA minimum contrast ratio for normal text on white */
const DEFAULT_MIN_CONTRAST_RATIO = 4.5;

/** Lightness decrement per iteration when darkening for contrast */
const LIGHTNESS_STEP = 2;

/** Saturation increase applied before the darkening loop */
const SATURATION_BOOST = 14;

/** Fallback lightness when no candidate meets the contrast threshold */
const GUARANTEED_READABLE_LIGHTNESS = 0;

/**
 * Converts a hex color string to RGB values
 * @param hex - Hex color string (with or without #)
 * @returns RGB object or null if invalid hex
 */
export const hexToRgb = (hex: string): RGB | null => {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result
		? {
				r: Number.parseInt(result[1], 16),
				g: Number.parseInt(result[2], 16),
				b: Number.parseInt(result[3], 16)
			}
		: null;
};

/**
 * Calculates WCAG relative luminance for an RGB color
 * @param color - RGB color values
 * @returns Relative luminance between 0 and 1
 */
const getRelativeLuminance = ({ r, g, b }: RGB): number => {
	const normalize = (value: number): number => {
		const channel = value / 255;
		return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
	};

	const red = normalize(r);
	const green = normalize(g);
	const blue = normalize(b);

	return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

/**
 * Calculates the WCAG contrast ratio between two RGB colors
 * @param foreground - Foreground RGB color
 * @param background - Background RGB color
 * @returns Contrast ratio (1 = identical, 21 = max for black on white)
 */
export const getContrastRatio = (foreground: RGB, background: RGB): number => {
	const foregroundLuminance = getRelativeLuminance(foreground);
	const backgroundLuminance = getRelativeLuminance(background);
	const lighter = Math.max(foregroundLuminance, backgroundLuminance);
	const darker = Math.min(foregroundLuminance, backgroundLuminance);

	return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Converts a hex color string to Tailwind-compatible HSL format
 * @param hex - Hex color string (with or without #)
 * @returns HSL string (e.g. "210 72% 45%") or null if invalid hex
 */
export const hexToHsl = (hex: string): string | null => {
	const rgb = hexToRgb(hex);
	if (!rgb) return null;

	const r = rgb.r / 255;
	const g = rgb.g / 255;
	const b = rgb.b / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	let h = 0;
	let s = 0;
	const l = (max + min) / 2;

	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

		switch (max) {
			case r:
				h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
				break;
			case g:
				h = ((b - r) / d + 2) / 6;
				break;
			case b:
				h = ((r - g) / d + 4) / 6;
				break;
		}
	}

	return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

/**
 * Parses a Tailwind-style HSL string into numeric components
 * @param hsl - HSL string (e.g. "210 72% 45%")
 * @returns Parsed HSL object or null if the format is invalid
 */
const parseHslString = (hsl: string): HSL | null => {
	const match = /^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/.exec(hsl.trim());
	if (!match) return null;

	return {
		h: Number.parseFloat(match[1]),
		s: Number.parseFloat(match[2]),
		l: Number.parseFloat(match[3])
	};
};

/**
 * Converts HSL components to RGB values
 * @param color - HSL color components
 * @returns RGB color values
 */
const hslToRgb = ({ h, s, l }: HSL): RGB => {
	const saturation = s / 100;
	const lightness = l / 100;
	const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
	const huePrime = h / 60;
	const secondLargest = chroma * (1 - Math.abs((huePrime % 2) - 1));
	let red = 0;
	let green = 0;
	let blue = 0;

	if (huePrime >= 0 && huePrime < 1) {
		red = chroma;
		green = secondLargest;
	} else if (huePrime >= 1 && huePrime < 2) {
		red = secondLargest;
		green = chroma;
	} else if (huePrime >= 2 && huePrime < 3) {
		green = chroma;
		blue = secondLargest;
	} else if (huePrime >= 3 && huePrime < 4) {
		green = secondLargest;
		blue = chroma;
	} else if (huePrime >= 4 && huePrime < 5) {
		red = secondLargest;
		blue = chroma;
	} else {
		red = chroma;
		blue = secondLargest;
	}

	const match = lightness - chroma / 2;

	return {
		r: Math.round((red + match) * 255),
		g: Math.round((green + match) * 255),
		b: Math.round((blue + match) * 255)
	};
};

/**
 * Formats HSL components as a Tailwind-compatible HSL string
 * @param color - HSL color components
 * @returns HSL string (e.g. "210 72% 45%")
 */
const formatHsl = ({ h, s, l }: HSL): string =>
	`${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%`;

/**
 * Converts a Tailwind-style HSL string to RGB values
 * @param hsl - HSL string (e.g. "210 72% 45%")
 * @returns RGB object or null if the format is invalid
 */
export const hslStringToRgb = (hsl: string): RGB | null => {
	const parsed = parseHslString(hsl);
	return parsed ? hslToRgb(parsed) : null;
};

/**
 * Derives an HSL color string for title text on white backgrounds.
 * Returns the primary color when contrast is already sufficient; otherwise
 * darkens it step-by-step until the minimum contrast ratio is met.
 * @param primaryHex - Tenant primary color as hex (with or without #)
 * @param minContrastRatio - Minimum contrast ratio against white (default: WCAG AA 4.5)
 * @returns Tailwind-style HSL string or null for invalid hex
 */
export const derivePrimaryTitleHsl = (
	primaryHex: string,
	minContrastRatio = DEFAULT_MIN_CONTRAST_RATIO
): string | null => {
	const primaryRgb = hexToRgb(primaryHex);
	if (!primaryRgb) return null;

	if (getContrastRatio(primaryRgb, WHITE) >= minContrastRatio) {
		return hexToHsl(primaryHex);
	}

	const baseHsl = parseHslString(hexToHsl(primaryHex) ?? '');
	if (!baseHsl) return null;

	const saturation = Math.min(100, baseHsl.s + SATURATION_BOOST);

	for (let lightness = baseHsl.l; lightness >= 0; lightness -= LIGHTNESS_STEP) {
		const candidate = { h: baseHsl.h, s: saturation, l: lightness };
		const candidateRgb = hslToRgb(candidate);

		if (getContrastRatio(candidateRgb, WHITE) >= minContrastRatio) {
			return formatHsl(candidate);
		}
	}

	return formatHsl({
		h: baseHsl.h,
		s: saturation,
		l: GUARANTEED_READABLE_LIGHTNESS
	});
};
